import { useEffect, useState } from "react";
import { constructFilterQuery } from "@qlik/qlik/src/lib/services/util";

/**
 * Custom hook for managing URL query parameters in a React component.
 * Call this hook in the component which needs URL query params handling with state passing in the
 * necessary arguments.
 * @param {Object} initialFilters - The current filter object.
 * @param {Object} categoryFieldMappingObj - The mapping of category titles to field IDs.
 * @param {Number} itemsPerPage - Number of items per page. Defaults to 9.
 * @param {Boolean} useTaxonomyKeyAsTitle - Whether to use Taxonomy names or mapping keys as filter titles.
 * @returns {[Object, Function]} - An array containing the filters object and the function to update the filter object.
 */
const useUrlQueryParamsState = (
    initialFilters,
    categoryFieldMappingObj,
    itemsPerPage = 9,
    useTaxonomyKeyAsTitle = false
) => {
    const [filters, setFilters] = useState(initialFilters);
    // update initial filter state from URL query params
    useEffect(() => {
        const windowObj = typeof window !== "undefined" ? window : undefined;
        if (!windowObj) return;
        const { searchParams } = new URL(windowObj.location.href);
        // set page as 0 based index in state from URL
        let page = parseInt(searchParams.get("page") || initialFilters.page, 10);
        page = typeof page === "number" && page > initialFilters.page ? page - 1 : initialFilters.page;

        const categoryFilters = Object.fromEntries(
            Object.entries(categoryFieldMappingObj).map(([categoryTitle, categoryFieldId]) => [
                useTaxonomyKeyAsTitle ? categoryFieldId : categoryTitle,
                searchParams
                    .get(categoryFieldId)
                    ?.split(",")
                    .filter(Boolean) || [],
            ])
        );

        const newFilters = {
            query: searchParams.get("query") || "",
            page,
            ...categoryFilters,
        };

        if (parseInt(searchParams.get("limit"))) {
            newFilters.limit = parseInt(searchParams.get("limit") || itemsPerPage, 10);
        }
        setFilters((prev) => ({ ...prev, ...newFilters }));
    }, [categoryFieldMappingObj]);

    // handle consecutive updates to the URL query with filter state
    useEffect(() => {
        const windowObj = typeof window !== "undefined" ? window : undefined;
        if (!windowObj) return;

        // set page as 1 based index in the URL from state
        const filtersObj = JSON.parse(JSON.stringify(filters));
        filtersObj.page += 1;
        // construct URL as string
        const searchParamsString = constructFilterQuery(filtersObj, categoryFieldMappingObj);

        windowObj.history.replaceState({}, null, `?${searchParamsString}`);
    }, [filters, categoryFieldMappingObj]);

    return [filters, setFilters];
};

export default useUrlQueryParamsState;
